import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Inbox,
    PenSquare,
    Settings,
    Users,
    LogOut,
    Activity,
    ArrowUpRight,
} from "lucide-react";
import { useAuth } from "./AuthContext";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_55a9f4cb-4c33-47bd-86a7-80fe678bbbdb/artifacts/wavtipa0_PROKSY%20RESEARCH%20%282%29.png";

export default function PortalShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/portal/login");
    };

    const links = [
        { to: "/portal", label: "Inbox", icon: Inbox, end: true },
        { to: "/portal/compose", label: "Compose", icon: PenSquare },
        { to: "/portal/mailbox-setup", label: "Mailbox", icon: Settings },
    ];
    if (user?.role === "admin") {
        links.push({ to: "/portal/admin", label: "Team", icon: Users });
    }

    return (
        <div className="min-h-screen bg-[#050B1A]">
            {/* Top bar */}
            <header className="fixed top-0 left-0 right-0 z-40 bg-[#050B1A]/85 backdrop-blur-xl border-b border-white/5">
                <div className="pr-container flex items-center justify-between py-3.5">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-lg overflow-hidden border border-white/10 bg-black/30">
                                <img
                                    src={LOGO_URL}
                                    alt="Proksy"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="leading-tight">
                                <p className="font-outfit font-bold text-sm text-white">
                                    PROKSY
                                </p>
                                <p className="font-outfit text-[9px] tracking-[0.32em] text-blue-400">
                                    PORTAL
                                </p>
                            </div>
                        </Link>
                        <span className="ml-3 hidden sm:inline-flex items-center gap-2 text-[10px] font-mono-pr text-emerald-300 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/20">
                            <Activity className="w-3 h-3" />
                            connected
                        </span>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex flex-col text-right leading-tight">
                            <span className="text-xs text-slate-300 font-outfit">
                                {user?.name || user?.email}
                            </span>
                            <span className="text-[10px] font-mono-pr uppercase tracking-widest text-blue-300">
                                {user?.role}
                            </span>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="btn-secondary py-2 px-3 text-xs"
                            data-testid="portal-logout"
                        >
                            <LogOut className="w-3.5 h-3.5" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            {/* Layout */}
            <div className="pt-20 pr-container grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 pb-16">
                <aside className="lg:sticky lg:top-24 self-start">
                    <nav
                        className="pr-glass rounded-2xl p-2 flex lg:flex-col gap-1 overflow-x-auto"
                        data-testid="portal-sidebar"
                    >
                        {links.map((l) => {
                            const Icon = l.icon;
                            return (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.end}
                                    className={({ isActive }) =>
                                        `relative flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-outfit font-medium whitespace-nowrap transition-colors ${
                                            isActive
                                                ? "bg-blue-500/15 border border-blue-400/30 text-white"
                                                : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                                        }`
                                    }
                                    data-testid={`sidebar-link-${l.label.toLowerCase()}`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {l.label}
                                </NavLink>
                            );
                        })}
                    </nav>
                    <Link
                        to="/"
                        className="mt-3 flex items-center gap-2 text-xs text-slate-500 hover:text-blue-300 px-3 py-2 font-mono-pr"
                    >
                        Back to public site
                        <ArrowUpRight className="w-3 h-3" />
                    </Link>
                </aside>

                <motion.main
                    key={typeof window !== "undefined" ? window.location.pathname : ""}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    className="min-w-0"
                >
                    {children}
                </motion.main>
            </div>
        </div>
    );
}
